import {Html} from '@react-three/drei';
import {useEffect, useRef, useState} from 'react';
import {AccommodationExercise} from '../../scenes/exercises/AccommodationExercise';
import {ConvergenceExercise} from '../../scenes/exercises/ConvergenceExercise';
import {Figure8Exercise} from '../../scenes/exercises/Figure8Exercise';
import {PursuitExercise} from '../../scenes/exercises/PursuitExercise';
import {SaccadesExercise} from '../../scenes/exercises/SaccadesExercise';
import {useStore} from '../../store/useStore';
import {DistanceSetting, ExerciseSettings, RangeSetting, TrainingMode} from '../../types/exercises';
import {logger} from '../../utils/logger';

// Create a store to share the time remaining information globally
export const workoutStore = {
  timeRemaining: 0,
  _isPaused: false,
  setTimeRemaining: (time: number) => {
    workoutStore.timeRemaining = time;
  },
  getTimeRemaining: () => workoutStore.timeRemaining,
  setPaused: (paused: boolean) => {
    workoutStore._isPaused = paused;
  },
  isPaused: () => workoutStore._isPaused,
};

interface WorkoutSequenceProps {
  exercises: {type: TrainingMode; duration: number; settings?: ExerciseSettings}[];
  onComplete: () => void;
  onEndExercise?: () => void;
}

// Custom hook for exercise timer management
const useExerciseTimer = (
  exercises: {type: TrainingMode; duration: number; settings?: ExerciseSettings}[],
  onComplete: () => void,
  isPaused: boolean
) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  // Initialize exercise time and handle completion
  useEffect(() => {
    logger.info('workout sequence exercises', exercises);
    if (exercises.length === 0) {
      onComplete();
      return;
    }

    const currentExercise = exercises[currentExerciseIndex];
    // Only set initial duration if we don't have a saved time
    if (lastTimeRef.current === null) {
      setTimeRemaining(currentExercise.duration);
      workoutStore.setTimeRemaining(currentExercise.duration);
    } else {
      // Restore the saved time
      setTimeRemaining(lastTimeRef.current);
      workoutStore.setTimeRemaining(lastTimeRef.current);
      lastTimeRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentExerciseIndex, exercises, onComplete]);

  // Handle the timer logic in a separate effect that depends on isPaused
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const currentExercise = exercises[currentExerciseIndex];
    const isUnlimited = currentExercise?.duration === -1;

    // Don't start timer for unlimited duration exercises
    if (!isPaused && !isUnlimited) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev <= 1 ? 0 : prev - 1;
          workoutStore.setTimeRemaining(newTime);

          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }

            if (currentExerciseIndex < exercises.length - 1) {
              setIsTransitioning(true);
              setTimeout(() => {
                setCurrentExerciseIndex(prev => prev + 1);
                setIsTransitioning(false);
              }, 1000);
              return 0;
            } else {
              onComplete();
              return 0;
            }
          }
          return newTime;
        });
      }, 1000);
    } else {
      // Save the current time when pausing (but not for unlimited duration)
      if (!isUnlimited) {
        lastTimeRef.current = timeRemaining;
      }
      logger.info(isUnlimited ? 'Timer not started for unlimited duration' : 'Timer interval cleared because isPaused is true');
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaused, currentExerciseIndex, exercises, onComplete, timeRemaining]);

  return {currentExerciseIndex, timeRemaining, isTransitioning};
};

// Component to render the appropriate exercise based on type
const ExerciseRenderer = ({
  exercise,
  onEndExercise,
}: {
  exercise: {type: TrainingMode; settings?: ExerciseSettings};
  onEndExercise?: () => void;
}) => {
  const exerciseProps = {
    ...(exercise.settings || {
      eyeToTrain: 'both',
      isRedGreen: false,
      isMFBF: false,
      isOverlay: false,
      speed: 7,
      range: 'medium' as RangeSetting,
      distance: 'middle' as DistanceSetting,
    }),
    onEndExercise,
  };

  try {
    switch (exercise.type) {
      case 'saccades':
        return <SaccadesExercise {...exerciseProps} />;
      case 'pursuit':
        return <PursuitExercise {...exerciseProps} />;
      case 'convergence':
        return <ConvergenceExercise {...exerciseProps} />;
      case 'accommodation':
        return <AccommodationExercise {...exerciseProps} />;
      case 'figure8':
        return <Figure8Exercise {...exerciseProps} />;
      default:
        logger.warn(`Unknown exercise type: ${exercise.type}`);
        return null;
    }
  } catch (error) {
    logger.error(`Error rendering exercise ${exercise.type}:`, error);
    return null;
  }
};

export const WorkoutSequence: React.FC<WorkoutSequenceProps> = ({exercises, onComplete, onEndExercise}) => {
  // Get timer paused state directly from the Zustand store
  const isTimerPaused = useStore(state => state.isTimerPaused);

  const {currentExerciseIndex, timeRemaining, isTransitioning} = useExerciseTimer(exercises, onComplete, isTimerPaused);

  // Update the legacy workoutStore with the current time remaining
  useEffect(() => {
    workoutStore.setTimeRemaining(timeRemaining);
  }, [timeRemaining]);

  // Log pause state changes from the store
  useEffect(() => {
    logger.info(`WorkoutSequence detected timer pause state change: ${isTimerPaused}`);
  }, [isTimerPaused]);

  if (exercises.length === 0) return null;

  const currentExercise = exercises[currentExerciseIndex];
  const isUnlimited = currentExercise.duration === -1;

  return (
    <>
      <ExerciseRenderer exercise={currentExercise} onEndExercise={onEndExercise} />
      <Html position={[0, 1.5, -1]} center>
        <div style={{color: 'white', fontSize: '24px', textAlign: 'center'}}>
          {isTransitioning ? 'Next exercise...' : isUnlimited ? 'Duration: ∞' : `Time remaining: ${timeRemaining}s`}
        </div>
      </Html>
    </>
  );
};
